import { Form, Popover, Typography, Alert } from 'antd';
import _, { isEmpty } from 'lodash';
import BaseModalForm from 'components/BaseModalForm';
import { MarkdownFormatter } from 'components/MarkupFormatter';
import { MODAL_SIZE_MEDIUM } from 'constants/Layout';
import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { FieldTypes, checkCharLimit } from 'utils/formUtils';
import { InfoIcon } from 'components/Icons';
import { marked } from "marked";
import htmlToMarkDown from "utils/htmlToMarkDownUtil";
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { PermissionSetMap } from 'constants/RBACConstants';
import { hasPermission } from 'utils/authService';
import { getFreshServiceCustomNestedFields } from './genericIntegrationTicketUtils';
import { commonTemplateFieldsValidator } from './UnifiedTicketTemplate/utils';
import { CUSTOM_VARIABLES } from './genericIntegrationTicketConstants';
import FindingVariablesTablePopover from 'components/FindingFieldsPopoverContent/FindingVariablesTablePopover';

const TemplateDesc = <>Ticket Description (<Typography.Link href='https://www.markdownguide.org/' target='_blank'>Markdown</Typography.Link>&nbsp;Supported except heading 1 & 2)</>;
// JIRA has datetime and Azure has dateTime
const slaDueDateTemplateSupport = ["datepicker", "dateTime", "datetime"];
const templateSupportedCustomFields = ['textfield', 'textarea', 'select', 'string', "gh-epic-label", "integer", "float", "double", "decimal", ...slaDueDateTemplateSupport];
const templateSupportedCustomFieldsDataTypes = ['number'];

function removeSpacesAroundInterpolationInUrls(str) {
    // Define the regular expression pattern to match spaces around ${ and } inside URLs
    const pattern = /(\[[^\]]+\]\(\s*[^)]*?\$\{\s*[^}]*?\s*\}\s*[^)]*?\))/g;

    // Define the regular expression pattern to match a single space between / and ${
    const pattern2 = /\/ (\${)/g;
  
    // Use the replace() function to remove spaces around ${ and } inside URLs
    const newStr = str.replace(pattern, (match) => {
      return match.replace(/\s*(\$[{])\s*|\s*([}])\s*/g, (match, openBrace, closeBrace) => {
        if (openBrace) {
          return '${';
        } else if (closeBrace) {
          return '}';
        }
      });
    });

    return newStr.replace(pattern2, '/${');
  }
  

const TicketTemplateForm = React.forwardRef((props, ref) => {
    const { configId, isConfigLevel = false } = props;
    const [form] = Form.useForm();
    const [data, setData] = useState(null);
    const [option, setOption] = useState(0);
    const [supportedCustomFields, setSupportedCustomFields] = useState([]);

    const onChange = useCallback((values) => {
        if (values.hasOwnProperty("option")) {
            setOption(values.option);
        }
    }, []);
    const templateID = data?.id;

    useEffect(() => {
        const { cancel, promise } = ToolsIntegrationsAPI[isConfigLevel ? "getTicketTemplateByLoginConfigId" : "getTicketTemplateByConfigId"](configId);
        promise.then((data) => {
            if(data){
                form.setFieldsValue({
                    title: data.title || "${finding.title}",  
                    description: (data.descriptionMarkdown || (data.description ? htmlToMarkDown(data.description) : "${finding.description}\n${finding.armorcodeurl}")),  
                    enable: !!data.enable
                });
                
                if (!_.isEmpty(data.fields)) {
                    let indexOfFieldWithNestedFields;
                    let nestedFields = [];
                    const supportedFields = _.filter(data.fields, (entry, index) => {
                        if(entry.nestedFields){
                            indexOfFieldWithNestedFields = index;
                            nestedFields = getFreshServiceCustomNestedFields(entry);
                        }
                        if (!entry.active) {
                            return false;
                        }
                        if (templateSupportedCustomFields.indexOf(entry.type) !== -1 || templateSupportedCustomFieldsDataTypes.indexOf(entry.dataType) !== -1) {
                            form.setFieldsValue({
                                [entry.key]: entry.defaultValue?.name
                            });
                            return true;
                        }
                    });
                    if(!isEmpty(nestedFields)){
                        supportedFields.splice(indexOfFieldWithNestedFields + 1, 0, ...nestedFields)
                        //set form values with nested fields
                        nestedFields.forEach((field) => {
                            form.setFieldsValue({[field.key]: field.defaultVal})
                        });
                    }
                    setSupportedCustomFields(supportedFields);
                }

                setData(data);
            } else {
                setData({});
            }
        })
        return cancel;
    }, [configId, form, isConfigLevel])

    const onSave = useCallback((fieldsValue) => {
        const customFieldsPayload = supportedCustomFields.map(entry => ({
            ..._.omit(entry, ['defaultValue']),
            ...(_.has(fieldsValue, entry.key) && !_.some(entry.allowedValues, { name: fieldsValue[entry.key] }) && {defaultVal: fieldsValue[entry.key] })
        }))

        const descriptionText = _.isUndefined(fieldsValue.description) ? "" : fieldsValue.description;

        return ToolsIntegrationsAPI[isConfigLevel ? "createOrUpdateGlobalTicketTemplate" : "createOrUpdateTicketTemplate"]({
            id: templateID,
            [isConfigLevel ? "loginConfigId" : "configId"]: configId,
            title: _.isUndefined(fieldsValue.title) ? "" : fieldsValue.title,
            enable: fieldsValue.enable,
            description: marked.parse(descriptionText),
            descriptionMarkdown: descriptionText,
            customFields: customFieldsPayload
        }).promise;
    }, [supportedCustomFields, isConfigLevel, templateID, configId]);


    const EditTemplateTitle = "Ticket Template" ;

    const TemplateTitle = "Ticket Title / Summary";


    const emptyPreviewText = "Nothing to Preview";

    const mentionsFieldProps = useMemo(() => ({
        prefix: '$',
        placement: 'bottom',
        options: templateFields.map(feilds => `{${feilds}}`)
    }), []);

    const chekForURL = useCallback((field, value) => {
        const modifiesValue = removeSpacesAroundInterpolationInUrls(value);
        form.setFieldsValue({
            [field]: modifiesValue
        });
    }, [form]);

    // const option = form.getFieldValue("option") || 0;
    const fields = useMemo(() => ([
    {
        name: '',
        label: '',
        render: () => <Alert type="info" className='m-b-xs' showIcon message={<>To learn more about scope of the template <Typography.Link href={`${window.location.origin}/api/jwt/zendesk/sso?return_to=https://support.armorcode.com/hc/en-us/articles/19598074383123#jira-ticket-template`} target="_blank"> click here. </Typography.Link></>} />
    },
    {
        name: "enable",
        label: "Enable",
        inline: true,
        type: FieldTypes.SWITCH,
    },
    {
        name: "option",
        type: FieldTypes.RADIO,
        options: [{ label: "Write", value: 0 }, { label: "Preview", value: 1 }]
    },
    ...(option ? [{
        name: '',
        label: '',
        render: () => <Alert type="warning" className='m-b-xs' showIcon message="Sample values are displayed in preview mode." />
    }] : []),
    {
        name: "title",
        label: <>{TemplateTitle}{option ? '' : findingFieldsPopover}</>,
        placeholder: TemplateTitle,
        type: FieldTypes.MENTIONS,
        props: {
            ...mentionsFieldProps,
            autoFocus: true,
            onChange: (value) => chekForURL("title", value)
        },
        render: option ? () => {
            const title = _.trim(form.getFieldValue("title"));
            return parseFindingVariables(title) || emptyPreviewText;
        } : null,
        rules: [{
            validator: findingFieldValidator
        }, {
            required: true,
            message: 'Title is required.'
        }, () => checkCharLimit(100)],
    }, {
        name: "description",
        label: <>{TemplateDesc}{option ? '' : findingFieldsPopover}</>,
        placeholder: "Description",        
        type: FieldTypes.MENTIONS,
        props: {
            rows: 10,
            ...mentionsFieldProps,
            onChange: (value) => chekForURL("description", value)
        },
        render: option ? () => {
            const description = _.trim(form.getFieldValue("description"));
            return description ? <MarkdownFormatter sanitizeMarkDown={true} markdown={parseFindingVariables(description)} /> : emptyPreviewText;
        } : null,
        rules: [{
            validator: findingFieldValidator
        }, {
            required: true,
            message: 'Description is required.'
        }],
    }, 
    ...(supportedCustomFields.length > 0 ? supportedCustomFields.map((entry => ({
        name: entry.key,
        label: <>{entry.name}{option ? '' : slaDueDateTemplateSupport.indexOf(entry.type) !== -1 ? slaDueDatePopover : findingFieldsPopover}</>,
        placeholder: entry.name,
        type: FieldTypes.MENTIONS,
        props: {
            rows: entry.type === 'textarea' ? 5 : 1,
            ...mentionsFieldProps,
            options: isDateTimeSupportedField(entry.type, slaDueDateTemplateSupport) ? ["{finding.resolutionDueDate}", "{finding.remediationDueDate}"] : mentionsFieldProps.options,
            onChange: (value) => chekForURL(entry.key, value)
        },
        render: option ? () => {
            const title = _.trim(form.getFieldValue(entry.key));
            return parseFindingVariables(title) || emptyPreviewText;
        } : null,
        rules: [{
            validator: (_, value) => isDateTimeSupportedField(entry.type, slaDueDateTemplateSupport) ? dateTimeFieldVariablesValidator(_, value) : findingFieldValidator(_, value) 
        }, () => checkCharLimit(100)],
    }))) : [])
]), [option, mentionsFieldProps, supportedCustomFields, chekForURL, form]);

    return (
        <BaseModalForm
            loading={_.isNull(data)}
            form={form}
            ref={ref}
            modalProps={{
                keyboard: false,
                width: MODAL_SIZE_MEDIUM,
                style: { top: 20 },
                disabled: !hasPermission(PermissionSetMap.EDIT_TOOL_CONFIG),
                // className: "modal-full-height"
            }}
            onOk={onSave}
            title={EditTemplateTitle}
            afterClose={props.afterClose}
            fields={fields}
            onValuesChange={onChange}
            initialValues={{ 
                option,
                // title: "${finding.title}", // eslint-disable-line no-template-curly-in-string
                // description: "${finding.description}", // eslint-disable-line no-template-curly-in-string
                enable: false
            }}
        />
    )
});
export default TicketTemplateForm;

export const findingFieldValidator = (e, value) => {
    return commonTemplateFieldsValidator(e, value, templateFields);
};

const dateTimeFieldVariablesValidator = (e, value) => {
    return commonTemplateFieldsValidator(e, value, ["finding.resolutionDueDate", "finding.remediationDueDate"]);
};

const isDateTimeSupportedField = (type, dateTimeSupportedFields) => {
    if(dateTimeSupportedFields.indexOf(type) !== -1){
        return true;
    }
    return false;
};

export const templateFieldsDescription = {
    'finding.id': 'Finding ID',
    'finding.title': 'Summary',
    'finding.status': 'Status',
    'finding.severity.value': 'Severity',
    'finding.toolSeverity': 'Tool Severity',
    'finding.category': 'Category',
    'finding.findingCategory': 'Tool Finding Category',
    'finding.impact': 'Impact',
    'finding.mitigation': 'Mitigation',
    'finding.description': 'Description',
    'finding.stepsToReproduce': 'Steps To Reproduce',
    'finding.evidence': 'Evidence',
    'finding.sourceCodeDetails.filePath': 'Source File Path',
    'finding.sourceCodeDetails.fileName': 'Source File Name',
    'finding.sourceCodeDetails.lineNumber': 'Source File Line Number',
    'finding.toolName': 'Source',
    'finding.scanType': 'Scan Type',
    'finding.product.name': 'Product',
    'finding.subProduct.name': 'Sub-Product',
    'finding.envName': 'Environment',
    'finding.securityOwner.name': 'Security Owner',
    'finding.owner.name': 'Owner',
    'finding.developer': 'Developer',
    'finding.resolutionDueDate': 'Resolution Due Date',
    'finding.remediationDueDate': 'Remediation Due Date',
    'finding.source.ref': 'Tool Finding URL',
    'finding.additionalDetails.repositoryName': 'Repository Name',
    'finding.additionalDetails.repositoryUrl': 'Repository URL',
    'finding.cves': 'CVE',
    'finding.cwes': 'CWE',
    'finding.tags': 'Tags',
    'finding.policy': 'Compliance',
    'finding.imageName': 'Image Name',
    'finding.imageTag': 'Image Tag',
    'finding.componentName': 'Component Name',
    'finding.componentVersion': 'Component Version',
    'finding.hostName': 'Device',
    'finding.toolTitle': 'Tool Title',
    'finding.toolId': "Tool Finding ID",
    'finding.impactScore': 'Impact Score',
    'finding.findingScore': 'Finding Score',
    'finding.aatiScore': 'AATI Score',
    'finding.cvssVector': 'CVSS Vector',
    'finding.cvssBaseScore': 'Base Score',
    'finding.exploitabilityScore': 'Exploitability Score',
    'finding.toolDescription': 'Tool Description',
    'finding.fileName': 'File Name',
    'finding.tag.<key>': 'Tag Name',
    'finding.armorcodeurl': 'Link To ArmorCode Finding',
    "finding.riskRating" : "Risk Rating",
    "finding.customField.<labelName>": "Finding Custom Field Label Name",
    "finding.cves.nvdurl": "CVE NVD URL",
    "finding.riskScore": "Finding Risk Score",
    "finding.ipAddress": "IP Address",
    "finding.remediationAction": "Remediation Action",
    "finding.remediationGroup": "Remediation Group",
    "finding.triageDueDate": "Triage Due Date",
    "finding.team": "Team",
    "finding.endPointDetails.uri": "Endpoint URI",
    "finding.ownerEmail": "Owner Email",
    "finding.derivedAttributes.<attributeName>": "Attribute Name",
    "finding.foundOnDate": "Finding Found On Date",
    "finding.exploitAvailable": "Finding Exploit Available",
    "finding.componentFixVersion" : "Component Fix Version"
};  

const dummyValues = {
    'finding.id': '12345',
    'finding.title': 'CVE-2016-9841 : rsync',
    'finding.status': 'Open',
    'finding.severity.value': 'High',
    'finding.toolSeverity': 'High',
    'finding.category': 'Vulnerability',
    'finding.findingCategory': 'Vulnerability',
    'finding.impact': 'Impact',
    'finding.mitigation': 'Fixed in 10.1.37',
    'finding.description': 'Vulnerability in the MySQL Server product of Oracle MySQL (component: Server: Stored Procedure).',
    'finding.stepsToReproduce': '1. step 1',
    'finding.evidence': 'Evidence example',
    'finding.sourceCodeDetails.filePath': '//package/1.5.4/index.js',
    'finding.sourceCodeDetails.fileName': 'index.js',
    'finding.sourceCodeDetails.lineNumber': '455',
    'finding.toolName': 'Prisma Cloud Twistlock',
    'finding.toolId': '4567',
    'finding.scanType': 'SAST',
    'finding.product.name': 'ArmorCode 1',
    'finding.subProduct.name': 'ArmorCode Sub-Product',
    'finding.envName': 'Production',
    'finding.securityOwner.name': 'Luke Skywalker',
    'finding.owner.name': 'Luke Skywalker',
    'finding.developer': 'Luke Skywalker',
    'finding.resolutionDueDate': '05/15/2022',
    'finding.source.ref': 'www.google.com',
    'finding.additionalDetails.repositoryName': 'armorcode/qa',
    'finding.additionalDetails.repositoryUrl': 'github.com/armorcode',
    'finding.cves': 'CVE-2016-9841',
    'finding.cwes': '2056',
    'finding.tags': 'tag, tag2',
    'finding.policy': 'Custom policy',
    'finding.impactScore': '3',
    'finding.findingScore': '5',
    'finding.aatiScore': '7.3',
    'finding.cvssVector': 'CVSS:3.1/AV:A/AC:L/PR:L/UI:R/I:L/C:L/S:U/A:L',
    'finding.cvssBaseScore': '2.5',
    'finding.exploitabilityScore': '5',
    'finding.toolDescription': 'Vulnerability in the MySQL Server product of Oracle MySQL (component: Server: Stored Procedure).',
    'finding.fileName': 'index.js',
    'finding.tag.<key>': 'TestTag',
    'finding.armorcodeurl': 'www.armorcode.com',
    "finding.riskRating" : "Risk Rating",
    "finding.customField.<labelName>": "Finding Custom Field Label Name",
    "finding.cves.nvdurl": "CVE NVD URL",
    "finding.riskScore": "Finding Risk Score",
    "finding.ipAddress": "IP Address",
    "finding.triageDueDate": "Triage Due Date",
    "finding.team": "Team",
};

export const templateFields = Object.keys(templateFieldsDescription);
export const templateFieldsWithBrackets = Object.keys(templateFieldsDescription).map(field => `$\{${field}}`);
export const findingFieldsPopover = <FindingVariablesTablePopover 
                                        fields={templateFields} fieldsDescription={templateFieldsDescription}/>
export const slaDueDatePopover = <Popover content={<div style={{ maxHeight: 500, overflowY: 'auto' }}>
    <b>Supported Finding Fields:</b>
    <br/>
    {"{finding.resolutionDueDate}"}
    <br/>
    {"{finding.remediationDueDate}"}
</div>} placement="right"><InfoIcon className="m-l-xs"/></Popover>;

export const parseFindingVariables = (text) => {
    const regex = /\${(.*?)}/gm;
    const newText = _.replace(text, regex, (match) => {
        const variable = match.split('{').pop().split('}').shift();
        const isCustomFieldLabelOrTag = variable.indexOf('finding.customField.') !== -1 || variable.indexOf('finding.tag.') !== -1;
        const customVariable = CUSTOM_VARIABLES.find((cv) => variable.indexOf(cv) !== -1);
        return isCustomFieldLabelOrTag ? `Value of "${variable.split(customVariable).pop()}"` : dummyValues[variable];
    });
    
    return newText;
}
